import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { CheckCheck, Megaphone, Sparkles, Wand2, Wrench, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useMobileDialogViewport } from '@/hooks/useMobileDialogViewport';
import { useReleaseReadState } from '@/hooks/useReleaseReadState';
import { releaseReadKey } from '@/lib/releaseReadState';
import { releaseNotes, type ReleaseNote } from '@/config/releaseNotes';

const changeGroups = [
  { kind: 'new', label: 'New', icon: Sparkles },
  { kind: 'improved', label: 'Improved', icon: Wand2 },
  { kind: 'fixed', label: 'Fixed', icon: Wrench },
] as const;

function releaseDate(date: string) {
  return new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
}

function ScopedWhatsNew({
  scopeKey,
  notes,
}: {
  scopeKey: string | null;
  notes: readonly ReleaseNote[];
}) {
  const [open, setOpen] = useState(false);
  const latestId = notes[0]?.id;
  const { unread, markViewed } = useReleaseReadState(scopeKey, latestId);
  const viewport = useMobileDialogViewport(open);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (next) markViewed();
        setOpen(next);
      }}
    >
      <Dialog.Trigger asChild>
        <button
          type="button"
          title="What's new"
          aria-label={`What's new${unread ? ', new updates' : ''}`}
          className="relative inline-flex h-11 min-w-11 shrink-0 items-center justify-center gap-2 rounded-xl px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Megaphone className="h-4 w-4" aria-hidden="true" />
          <span className="hidden lg:inline">What's new</span>
          {unread && (
            <>
              <span
                aria-hidden="true"
                className="absolute right-2 top-2 h-2 w-2 rounded-full bg-primary ring-2 ring-card lg:hidden"
              />
              <span
                aria-hidden="true"
                className="hidden rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-cyan-700 dark:text-primary lg:inline"
              >
                New
              </span>
            </>
          )}
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          data-release-notes
          style={viewport}
          className="fixed inset-x-0 top-[var(--dialog-mobile-top,0px)] z-50 flex h-[var(--dialog-mobile-height,100dvh)] min-w-0 flex-col overflow-hidden bg-card shadow-2xl sm:inset-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:max-h-[90dvh] sm:w-[calc(100vw-3rem)] sm:max-w-2xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:border sm:border-border"
        >
          <header className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-4 pb-4 pt-[max(1rem,env(safe-area-inset-top))] sm:p-5">
            <div className="flex min-w-0 items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-cyan-700 dark:text-primary">
                <Megaphone className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <Dialog.Title className="text-lg font-semibold">What's new</Dialog.Title>
                <Dialog.Description className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  Updates included in this version of KLOTHIQ.
                </Dialog.Description>
              </div>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close release notes"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </Dialog.Close>
          </header>
          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain p-4 sm:p-5">
            {notes.length ? (
              notes.map((release, index) => (
                <article key={release.id} aria-label={release.title} className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <time dateTime={release.date}>{releaseDate(release.date)}</time>
                      {index === 0 && (
                        <span className="rounded-full border border-primary/20 bg-primary/5 px-2 py-1 font-medium text-cyan-700 dark:text-primary">
                          Latest update
                        </span>
                      )}
                    </div>
                    <h3 className="break-words text-lg font-semibold leading-snug">
                      {release.title}
                    </h3>
                  </div>
                  {changeGroups.map(({ kind, label, icon: Icon }) => {
                    const changes = release.changes.filter((change) => change.kind === kind);
                    return changes.length ? (
                      <section
                        key={kind}
                        className="space-y-2 rounded-2xl border border-border bg-background/40 p-3 sm:p-4"
                      >
                        <h4 className="flex items-center gap-2 text-xs font-semibold text-cyan-700 dark:text-primary">
                          <Icon className="h-4 w-4" aria-hidden="true" />
                          {label}
                        </h4>
                        <ul className="divide-y divide-border/60">
                          {changes.map((change) => (
                            <li key={change.id} className="py-3 first:pt-1 last:pb-0">
                              <p className="break-words text-sm font-semibold">{change.title}</p>
                              <p className="mt-1.5 break-words text-sm leading-relaxed text-muted-foreground">
                                {change.description}
                              </p>
                            </li>
                          ))}
                        </ul>
                      </section>
                    ) : null;
                  })}
                </article>
              ))
            ) : (
              <div className="py-12 text-center">
                <CheckCheck
                  className="mx-auto mb-3 h-8 w-8 text-muted-foreground"
                  aria-hidden="true"
                />
                <h3 className="font-semibold">No release notes yet</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Updates will appear here when they are added.
                </p>
              </div>
            )}
          </div>
          <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-border px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-4">
            <p className="text-xs text-muted-foreground">You can revisit these notes anytime.</p>
            <Dialog.Close asChild>
              <button
                type="button"
                className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Got it
                <CheckCheck className="h-4 w-4" aria-hidden="true" />
              </button>
            </Dialog.Close>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default function WhatsNew({ notes = releaseNotes }: { notes?: readonly ReleaseNote[] }) {
  const { profile, organization, access, isAuthenticated } = useAuth();
  if (!isAuthenticated || !access?.ready || !profile?.id || !organization?.id) return null;
  const scopeKey = notes[0] ? releaseReadKey(organization.id, profile.id, notes[0].id) : null;
  return (
    <ScopedWhatsNew
      key={JSON.stringify([organization.id, profile.id, access.access_version, notes[0]?.id])}
      scopeKey={scopeKey}
      notes={notes}
    />
  );
}
